import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { withTheme } from 'styled-components';
import { Text } from '../../Reusable/Text';
import { Flex, FlexColumn } from '../../Reusable/Container';
import { Skeleton } from 'antd';
import { apiPOSTReq } from '../../../Utils/api';
import environment from '../../../environment';
import { getDateByTZ } from '../../../Utils/util';
import moment from 'moment';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import LucideIcon from '../../Reusable/Button';
import hexToRgba from 'hex-to-rgba';

const PaymentHistoryChart = (props) => {
    const { theme, chartTitle = 'Payment History', height = 300, minWidth = '350px' } = props;
    
    // State management
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [dateRange, setDateRange] = useState({
        startDate: moment().subtract(5, 'months').startOf('month'),
        endDate: moment()
    });
    const [canNavigateForward, setCanNavigateForward] = useState(false);
    const [canNavigateBackward, setCanNavigateBackward] = useState(true);
    const [earliestDateWithData, setEarliestDateWithData] = useState(null);
    
    // Fetch payment history on component mount and when date range changes
    useEffect(() => {
        if (props.customerId) {
            fetchPaymentHistory();
        }
    }, [props.customerId, dateRange]);
    
    const fetchPaymentHistory = () => {
        if (!props.customerId) return;
        
        setLoading(true);
        
        const body = {
            "customerId": props.customerId,
            "fromDate": getDateByTZ(dateRange.startDate, "YYYY-MM-DD", "America/New_York"),
            "toDate": getDateByTZ(dateRange.endDate, "YYYY-MM-DD", "America/New_York"),
        };
        
        apiPOSTReq(`${environment.iveBaseUrl}/ive/bc/getPaymentHistory`, {}, body, (err, resp) => {
            setLoading(false);
            
            try {
                if (resp && resp.result) {
                    const data = processChartData(resp.chartData || {});
                    console.log("Processing chart data: ", resp.chartData);
                    console.log("Processed result: ", data);
                    setChartData(data);
                    
                    // Update earliest date with data if available
                    if (resp.chartData.earliestDateWithData) {
                        try {
                            const earliestDate = moment(resp.chartData.earliestDateWithData, "YYYY-MM-DD");
                            setEarliestDateWithData(earliestDate);
                            
                            // Direct calculation of backward navigation
                            const canGoBackward = earliestDate.isBefore(dateRange.startDate);
                            
                            // Check if there's data in the current window
                            const hasVisibleData = data.some(item => item.paid > 0 || item.billed > 0);
                            
                            // Set navigation state
                            setCanNavigateBackward(canGoBackward && hasVisibleData);
                            
                            // Can navigate forward if we're not at the current date
                            setCanNavigateForward(!isCurrentPeriod());
                        } catch (e) {
                            console.error('Error parsing earliest date:', e);
                            setCanNavigateBackward(false);
                        }
                    } else {
                        setCanNavigateBackward(false);
                    }
                } else {
                    throw Error(resp?.error || resp?.responseMessage || "Error fetching payment history");
                }
            } catch (error) {
                console.error("Error in payment history fetch:", error, resp);
                setChartData([]);
                setCanNavigateBackward(false);
            }
        });
    };
    
    // Check if we're looking at the current period (up to present)
    const isCurrentPeriod = () => {
        const currentDate = moment();
        const currentMonth = currentDate.month();
        const currentYear = currentDate.year();
        const endMonth = dateRange.endDate.month();
        const endYear = dateRange.endDate.year();
        
        // Check if endDate is current month or later
        return (currentYear === endYear && currentMonth === endMonth) || 
               dateRange.endDate.isAfter(currentDate);
    };
    
    // Navigate backward (older dates)
    const navigateBackward = () => {
        if (!canNavigateBackward) return;
        
        // Calculate the new date range - move back 6 months
        const newStartDate = dateRange.startDate.clone().subtract(6, 'months');
        const newEndDate = dateRange.endDate.clone().subtract(6, 'months');
        
        // If earliest data date exists, don't go before it
        if (earliestDateWithData && newStartDate.isBefore(earliestDateWithData)) {
            // Simply start at the earliest data date
            setDateRange({
                startDate: earliestDateWithData,
                endDate: moment(earliestDateWithData).clone().add(6, 'months')
            });
            return;
        }
        
        setDateRange({
            startDate: newStartDate,
            endDate: newEndDate
        });
    };
    
    // Navigate forward (newer dates)
    const navigateForward = () => {
        if (!canNavigateForward) return;
        
        const newEndDate = dateRange.endDate.clone().add(6, 'months');
        // Don't allow navigating beyond current date
        if (newEndDate.isAfter(moment())) {
            setDateRange({
                startDate: moment().clone().subtract(7, 'months'), // Use 7 months for a 7-month range
                endDate: moment()
            });
        } else {
            setDateRange({
                startDate: dateRange.startDate.clone().add(6, 'months'),
                endDate: newEndDate
            });
        }
    };
    
    // Process chart data from API response
    const processChartData = (apiData) => {
        const paymentHistory = apiData.paymentHistory || [];
        
        // Create a map of existing data
        const dataMap = new Map(
            paymentHistory.map(item => [
                `${item.year}-${item.month}`,
                {
                    paid: Number(item.totalPaidAmount),
                    billed: Number(item.totalBilledAmount)
                }
            ])
        );
        
        // Calculate all months in date range
        const startMonth = dateRange.startDate.month() + 1; // Convert to 1-indexed
        const startYear = dateRange.startDate.year();
        const endMonth = dateRange.endDate.month() + 1;     // Convert to 1-indexed
        const endYear = dateRange.endDate.year();
        
        console.log(`Processing date range: ${startYear}-${startMonth} to ${endYear}-${endMonth}`);
        
        // Create array of all months in range
        const monthMapping = [];
        let currentYear = startYear;
        let currentMonth = startMonth;
        
        // Build the array of months
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            monthMapping.push({ year: currentYear, month: currentMonth });
            
            // Move to next month
            currentMonth++;
            if (currentMonth > 12) {
                currentMonth = 1;
                currentYear++;
            }
        }
        
        console.log("Total months in range:", monthMapping.length);
        
        // ALWAYS use exactly 6 months
        // If we have more than 6 months, use the last 6
        // This ensures April data is included if it's in the range
        const monthsToUse = monthMapping.length > 6 
            ? monthMapping.slice(monthMapping.length - 6) // Take last 6 months
            : monthMapping;
        
        console.log("Month mapping:", monthsToUse.map(m => `${m.year}-${m.month}`));
        
        // Generate data array
        const months = [];
        for (const monthData of monthsToUse) {
            const key = `${monthData.year}-${monthData.month}`;
            const existingData = dataMap.get(key) || { paid: 0, billed: 0 };
            
            // Convert to 0-indexed for display
            const monthIndex = monthData.month - 1;
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            
            months.push({
                month: `${monthNames[monthIndex]} '${String(monthData.year).slice(2)}`,
                paid: existingData.paid,
                billed: existingData.billed
            });
        }
        
        // Return the months in chronological order
        return months;
    };
    
    // Custom shape for rounded bars
    const RoundedBar = (props) => {
        const { fill, x, y, width, height } = props;
        const radius = 5;
        
        // Don't render anything if height is 0
        if (height === 0 || !height) return null;
        
        return (
            <g>
                <path
                    d={`
                        M${x},${y + height - radius}
                        L${x},${y + radius}
                        Q${x},${y} ${x + radius},${y}
                        L${x + width - radius},${y}
                        Q${x + width},${y} ${x + width},${y + radius}
                        L${x + width},${y + height - radius}
                        Q${x + width},${y + height} ${x + width - radius},${y + height}
                        L${x + radius},${y + height}
                        Q${x},${y + height} ${x},${y + height - radius}
                        Z
                    `}
                    fill={fill}
                />
            </g>
        );
    };
    
    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '4px 8px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}>
                    <div>{label}</div>
                    {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(payload[0].value)}
                </div>
            );
        }
        return null;
    };
    
    // Date range label for display
    const dateRangeLabel = () => {
        // For 6-month display, when there are more than 6 months in the range,
        // we need to adjust the displayed start date to match what we're showing
        if (monthsBetween(dateRange.startDate, dateRange.endDate) > 6) {
            // Calculate the actual start date of visible data (6 months before end date)
            const adjustedStartDate = dateRange.endDate.clone().subtract(5, 'months');
            return `${adjustedStartDate.format('MMM YYYY')} - ${dateRange.endDate.format('MMM YYYY')}`;
        }
        
        // Otherwise use the full range
        return `${dateRange.startDate.format('MMM YYYY')} - ${dateRange.endDate.format('MMM YYYY')}`;
    };
    
    // Helper function to calculate months between two dates
    const monthsBetween = (startDate, endDate) => {
        return (endDate.year() - startDate.year()) * 12 + endDate.month() - startDate.month() + 1;
    };
    
    return (
        <FlexColumn style={{ minWidth }} gap='24px'>
            <Flex between centerH>
                <Text weight={500} size='18px' color={theme.colors.primary2}>{chartTitle}</Text>
                <Flex gap='16px' centerH>
                    <Text size='14px' color={theme.colors.gray600}>{dateRangeLabel()}</Text>
                    <Flex gap='8px'>
                        <LucideIcon 
                            icon={ChevronLeft} 
                            color={canNavigateBackward ? theme.colors.primary2 : hexToRgba(theme.colors.primary2, 0.2)}
                            size={18} 
                            onClick={navigateBackward}
                            style={{ cursor: canNavigateBackward ? 'pointer' : 'not-allowed' }}
                        />
                        <LucideIcon 
                            icon={ChevronRight} 
                            color={canNavigateForward ? theme.colors.primary2 : hexToRgba(theme.colors.primary2, 0.2)}
                            size={18} 
                            onClick={navigateForward}
                            style={{ cursor: canNavigateForward ? 'pointer' : 'not-allowed' }}
                        />
                    </Flex>
                </Flex>
            </Flex>
            <div style={{ width: '100%' }}>
                <Skeleton loading={loading} active title={false} paragraph={{ rows: 5 }}>
                    <ResponsiveContainer width="100%" height={height}>
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 0,
                                right: 0,
                                left: 0,
                                bottom: 15,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                height={60}
                                tick={{
                                    fill: '#6B7280',
                                    fontSize: 10
                                }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#6B7280', fontSize: 10 }}
                                tickFormatter={(value) => `$${value >= 1000 ? `${value / 1000}k` : value}`}
                            />
                            {/* <Tooltip 
                                content={<CustomTooltip />}
                                cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                                position={{ y: 0 }}
                                offset={0}
                                allowEscapeViewBox={{ x: false, y: true }}
                            /> */}
                            <Bar
                                dataKey="paid"
                                name="Paid Amount"
                                fill="#60A5FA"
                                shape={<RoundedBar />}
                                barSize={10}
                                isAnimationActive={false}
                                minPointSize={0}  // Prevent rendering of zero values
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Skeleton>
            </div>
        </FlexColumn>
    );
};

export default withTheme(PaymentHistoryChart);